import {
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import * as Yup from 'yup';
import AutoComplete from '../../../pages/Listings/Autocomplete';
import { AddressSelection } from '../../../types/AddressSelection';

export interface HomeSaleContingencyValues {
  contingent: boolean;
  contingentPropertyAddress: string;
  contingentPropertyListed: boolean | null;
  contingentPropertyUnderContract: boolean | null;
  contingentPropertyClosingDate: Date | null;
  contingentPropertyListDate: Date | null;
}

export const homeSaleContingencyInitialValues: Omit<
  HomeSaleContingencyValues,
  'contingent'
> = {
  contingentPropertyAddress: '',
  contingentPropertyListed: null,
  contingentPropertyUnderContract: null,
  contingentPropertyClosingDate: null,
  contingentPropertyListDate: null,
};

/** Yup fields for the home-sale details, required only while `contingent` is true. */
export const homeSaleContingencySchema = {
  contingentPropertyAddress: Yup.string().when('contingent', {
    is: true,
    then: (schema) =>
      schema.trim().required('Enter the address of the home you are selling'),
    otherwise: (schema) => schema.notRequired(),
  }),
  contingentPropertyListed: Yup.boolean()
    .nullable()
    .when('contingent', {
      is: true,
      then: (schema) =>
        schema.required('Tell us whether your home is listed for sale'),
    }),
  contingentPropertyUnderContract: Yup.boolean()
    .nullable()
    .when(['contingent', 'contingentPropertyListed'], {
      is: (contingent: boolean, listed: boolean | null) =>
        contingent && listed === true,
      then: (schema) =>
        schema.required('Tell us whether your home is under contract'),
    }),
  contingentPropertyClosingDate: Yup.date()
    .nullable()
    .typeError('Expected closing date must be a valid date')
    .when(
      [
        'contingent',
        'contingentPropertyListed',
        'contingentPropertyUnderContract',
      ],
      {
        is: (
          contingent: boolean,
          listed: boolean | null,
          underContract: boolean | null
        ) => contingent && listed === true && underContract === true,
        then: (schema) =>
          schema.required('Enter the expected closing date for your home'),
      }
    ),
  // Only checked while its picker is shown; a hidden stale value is never sent.
  contingentPropertyListDate: Yup.date()
    .nullable()
    .typeError('Planned list date must be a valid date')
    .when(['contingent', 'contingentPropertyListed'], {
      is: (contingent: boolean, listed: boolean | null) =>
        !(contingent && listed === false),
      then: (schema) => schema.transform(() => null),
    }),
};

// Date-only columns arrive as UTC midnight; read the calendar day so the
// picker doesn't show the day before in US time zones.
const fromDateOnly = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  const iso = value instanceof Date ? value.toISOString() : String(value);
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** Form values for an offer that already has home-sale contingency details. */
export const homeSaleContingencyValuesFromOffer = (offer: {
  contingentPropertyAddress?: string | null;
  contingentPropertyListed?: boolean | null;
  contingentPropertyUnderContract?: boolean | null;
  contingentPropertyClosingDate?: Date | string | null;
  contingentPropertyListDate?: Date | string | null;
}): Omit<HomeSaleContingencyValues, 'contingent'> => ({
  contingentPropertyAddress: offer.contingentPropertyAddress ?? '',
  contingentPropertyListed: offer.contingentPropertyListed ?? null,
  contingentPropertyUnderContract:
    offer.contingentPropertyUnderContract ?? null,
  contingentPropertyClosingDate: fromDateOnly(
    offer.contingentPropertyClosingDate
  ),
  contingentPropertyListDate: fromDateOnly(offer.contingentPropertyListDate),
});

/**
 * The details to save with the offer. Only the answers the buyer was actually
 * shown are kept, so switching the contingency off (or changing an earlier
 * answer) never leaves a stale address or date on the offer.
 */
export const toHomeSaleContingencyPayload = (
  values: HomeSaleContingencyValues
) => {
  if (!values.contingent) {
    return {
      contingentPropertyAddress: null,
      contingentPropertyListed: null,
      contingentPropertyUnderContract: null,
      contingentPropertyClosingDate: null,
      contingentPropertyListDate: null,
    };
  }
  const listed = values.contingentPropertyListed;
  const underContract =
    listed === true ? values.contingentPropertyUnderContract : null;
  return {
    contingentPropertyAddress: values.contingentPropertyAddress.trim() || null,
    contingentPropertyListed: listed,
    contingentPropertyUnderContract: underContract,
    contingentPropertyClosingDate:
      underContract === true ? values.contingentPropertyClosingDate : null,
    contingentPropertyListDate:
      listed === false ? values.contingentPropertyListDate : null,
  };
};

interface FormikSlice {
  values: HomeSaleContingencyValues;
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  submitCount: number;
  setFieldValue: (field: string, value: unknown) => unknown;
  setFieldTouched: (
    field: string,
    isTouched?: boolean,
    shouldValidate?: boolean
  ) => unknown;
}

interface Props {
  formik: FormikSlice;
}

const toRadioValue = (value: boolean | null) =>
  value === null ? '' : String(value);

const HomeSaleContingencyFields = ({ formik }: Props) => {
  const { values } = formik;

  const errorFor = (field: keyof HomeSaleContingencyValues) => {
    const shown = formik.touched[field] || formik.submitCount > 0;
    const message = formik.errors[field];
    return shown && typeof message === 'string' ? message : undefined;
  };

  const setBoolean =
    (field: keyof HomeSaleContingencyValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue(field, e.target.value === 'true');
      formik.setFieldTouched(field, true, false);
    };

  // A picked suggestion and free typing both land here, so an address the
  // autocomplete doesn't know is still kept as typed.
  const handleAddressInput = (text: string) => {
    formik.setFieldValue('contingentPropertyAddress', text);
  };

  const handleAddressSelect = (selection: AddressSelection | string | null) => {
    const text =
      typeof selection === 'string'
        ? selection
        : selection?.title || selection?.address || '';
    formik.setFieldValue('contingentPropertyAddress', text);
    formik.setFieldTouched('contingentPropertyAddress', true, false);
  };

  const addressError = errorFor('contingentPropertyAddress');
  const listedError = errorFor('contingentPropertyListed');
  const underContractError = errorFor('contingentPropertyUnderContract');
  const closingDateError = errorFor('contingentPropertyClosingDate');
  const listDateError = errorFor('contingentPropertyListDate');

  return (
    <Box
      data-testid="home-sale-contingency-fields"
      sx={{
        p: 2,
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Stack spacing={2}>
        <Typography variant="body1" fontWeight={500}>
          The home you&apos;re selling
        </Typography>

        <FormControl error={Boolean(addressError)} fullWidth>
          <FormLabel sx={{ mb: 1 }}>Address</FormLabel>
          <Box
            sx={{
              '& .MuiAutocomplete-root fieldset': { borderRadius: 1 },
              '& .MuiAutocomplete-root .MuiOutlinedInput-root': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <AutoComplete
              handleChange={handleAddressSelect}
              onInputValueChange={handleAddressInput}
              keepFullPastedAddress
              placeholder="Start typing the address"
              property={
                values.contingentPropertyAddress
                  ? ({
                      address: values.contingentPropertyAddress,
                    } as AddressSelection)
                  : null
              }
            />
          </Box>
          {addressError && <FormHelperText>{addressError}</FormHelperText>}
        </FormControl>

        <FormControl error={Boolean(listedError)}>
          <FormLabel id="contingentPropertyListed">
            Is it listed for sale?
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="contingentPropertyListed"
            name="contingentPropertyListed"
            value={toRadioValue(values.contingentPropertyListed)}
            onChange={setBoolean('contingentPropertyListed')}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
          {listedError && <FormHelperText>{listedError}</FormHelperText>}
        </FormControl>

        {values.contingentPropertyListed === true && (
          <FormControl error={Boolean(underContractError)}>
            <FormLabel id="contingentPropertyUnderContract">
              Is it under contract?
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="contingentPropertyUnderContract"
              name="contingentPropertyUnderContract"
              value={toRadioValue(values.contingentPropertyUnderContract)}
              onChange={setBoolean('contingentPropertyUnderContract')}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
            {underContractError && (
              <FormHelperText>{underContractError}</FormHelperText>
            )}
          </FormControl>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {values.contingentPropertyListed === true &&
            values.contingentPropertyUnderContract === true && (
              <DatePicker
                label="Expected closing date"
                value={values.contingentPropertyClosingDate}
                onChange={(value) => {
                  formik.setFieldValue('contingentPropertyClosingDate', value);
                  formik.setFieldTouched(
                    'contingentPropertyClosingDate',
                    true,
                    false
                  );
                }}
                disablePast
                sx={{ width: '100%', bgcolor: 'background.paper' }}
                slotProps={{
                  textField: {
                    required: true,
                    error: Boolean(closingDateError),
                    helperText: closingDateError,
                  },
                }}
              />
            )}

          {values.contingentPropertyListed === false && (
            <DatePicker
              label="Planned list date (optional)"
              value={values.contingentPropertyListDate}
              onChange={(value) => {
                formik.setFieldValue('contingentPropertyListDate', value);
                formik.setFieldTouched(
                  'contingentPropertyListDate',
                  true,
                  false
                );
              }}
              disablePast
              sx={{ width: '100%', bgcolor: 'background.paper' }}
              slotProps={{
                textField: {
                  error: Boolean(listDateError),
                  helperText: listDateError,
                },
              }}
            />
          )}
        </LocalizationProvider>
      </Stack>
    </Box>
  );
};

export default HomeSaleContingencyFields;
